/**
 * Распределение по статусам: часть к целому.
 *
 * Горизонтальная составная полоса — форма для part-to-whole с длинными
 * русскими названиями. Категориальная палитра, слоты назначаются в
 * фиксированном порядке и никогда не по кругу.
 *
 * Четыре серии в составной форме допустимы по проверке палитры, но обязывают
 * к прямым подписям — они здесь и есть, плюс легенда и табличный вид.
 * Между сегментами оставлен зазор в 2px цветом поверхности: без него
 * соседние заливки сливаются при цветовой слепоте.
 */

import { useState } from "react";

import { Button } from "../Button";

export interface BreakdownSlice {
  id: string;
  label: string;
  value: number;
}

/** Слоты назначаются по порядку появления, а не по величине: цвет следует
 *  за сущностью, а не за её рангом — иначе фильтр перекрашивает выживших. */
const SLOTS = ["var(--series-1)", "var(--series-2)", "var(--series-3)", "var(--series-4)"];

export function StatusBreakdown({
  title,
  slices,
  totalLabel = "всего",
}: {
  title: string;
  slices: BreakdownSlice[];
  totalLabel?: string;
}) {
  const [asTable, setAsTable] = useState(false);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <figure
      // Имя задаётся явно: figcaption содержит и заголовок, и кнопку
      // переключения вида, и доступное имя из него получилось бы
      // «Название Таблицей».
      aria-label={title}
      className="viz m-0 flex flex-col gap-3 rounded-lg border border-line bg-surface p-4"
    >
      <figcaption className="flex items-center justify-between gap-3">
        <h3 className="text-[13px] font-extrabold text-fg">{title}</h3>
        <span className="flex items-center gap-2">
          <span className="text-[13px] text-muted tabular-nums">
            {total} {totalLabel}
          </span>
          <Button size="sm" variant="ghost" onClick={() => setAsTable((value) => !value)}>
            {asTable ? "Диаграмма" : "Таблицей"}
          </Button>
        </span>
      </figcaption>

      {total === 0 ? (
        <p className="py-6 text-center text-[13px] text-faint">Нет задач за выбранный период</p>
      ) : asTable ? (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-line">
              <th scope="col" className="py-1.5 text-left text-[11px] font-bold text-faint uppercase">
                Статус
              </th>
              <th scope="col" className="py-1.5 text-right text-[11px] font-bold text-faint uppercase">
                Задач
              </th>
              <th scope="col" className="py-1.5 text-right text-[11px] font-bold text-faint uppercase">
                Доля
              </th>
            </tr>
          </thead>
          <tbody>
            {slices.map((slice) => (
              <tr key={slice.id} className="border-b border-line last:border-0">
                <td className="py-1.5 text-fg">{slice.label}</td>
                <td className="py-1.5 text-right text-fg tabular-nums">{slice.value}</td>
                <td className="py-1.5 text-right text-muted tabular-nums">
                  {((slice.value / total) * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <>
          <div className="flex h-7 w-full gap-0.5 overflow-hidden rounded-md" role="img"
            aria-label={slices
              .map((s) => `${s.label}: ${String(s.value)}`)
              .join(", ")}
          >
            {slices.map((slice, index) => {
              if (slice.value === 0) return null;
              const share = (slice.value / total) * 100;
              return (
                <div
                  key={slice.id}
                  className="flex items-center justify-center overflow-hidden"
                  style={{ width: `${String(share)}%`, background: SLOTS[index % SLOTS.length] }}
                  title={`${slice.label}: ${String(slice.value)}`}
                >
                  {/* Подпись прямо на сегменте — правило рельефа. Прячется,
                      только если физически не помещается. */}
                  {share > 8 && (
                    <span className="px-1 text-[11px] font-bold text-white tabular-nums">
                      {slice.value}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
            {slices.map((slice, index) => (
              <li key={slice.id} className="flex items-center gap-1.5 text-[12px]">
                <span
                  className="size-2.5 shrink-0 rounded-[3px]"
                  style={{ background: SLOTS[index % SLOTS.length] }}
                  aria-hidden
                />
                {/* Подписи носят текстовые токены, а не цвет серии. */}
                <span className="text-muted">{slice.label}</span>
                <span className="font-semibold text-fg tabular-nums">{slice.value}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </figure>
  );
}
